import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Lock, Megaphone, LinkIcon } from "lucide-react";
import { authAxios } from "@/context/AuthContext";
import { ADS, TIER_ORDER } from "@/data/growthContent";

export default function AdsPage() {
  const [tier, setTier] = useState(null);
  const [handle, setHandle] = useState("");

  useEffect(() => {
    authAxios.get("/user/dashboard").then((r) => {
      setTier(r.data.tier.name);
      setHandle(r.data.user.handle || "");
    }).catch(console.error);
  }, []);

  const shareUrl = `${window.location.origin}/go/${handle}`;
  const tierIdx = TIER_ORDER.indexOf(tier);

  const copyAd = (ad) => {
    navigator.clipboard.writeText(ad.body.replaceAll("{{link}}", shareUrl))
      .then(() => toast.success("Ad copied with your link — paste it anywhere!"))
      .catch(() => toast.error("Copy failed"));
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => toast.success("Your link is copied!"));
  };

  if (tier === null) return <main className="max-w-4xl mx-auto px-4 py-24 text-center text-slate-400">Loading ad library…</main>;

  return (
    <main data-testid="ads-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-600 mb-2 flex items-center gap-1.5"><Megaphone className="w-4 h-4" /> Ad Library</p>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">Ready-made ads. Your link inside.</h1>
      <p className="text-slate-600 max-w-2xl mb-8 leading-relaxed">Every copy button injects your personal link automatically — paste straight into any social platform. Higher tiers unlock stronger creatives.</p>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-slate-900 rounded-xl p-5 mb-12">
        <LinkIcon className="w-4 h-4 text-emerald-400 shrink-0" />
        <code data-testid="my-share-link" className="text-sm text-emerald-400 flex-1 break-all">{shareUrl}</code>
        <button onClick={copyLink} data-testid="copy-share-link-button"
          className="flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors duration-200">
          <Copy className="w-3.5 h-3.5" /> Copy link
        </button>
      </div>

      {TIER_ORDER.map((t, ti) => {
        const ads = ADS.filter((a) => a.tier === t);
        const locked = ti > tierIdx;
        return (
          <section key={t} className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl tracking-tight mb-5 flex items-center gap-2">
              {t} ads {locked && <span className="flex items-center gap-1 text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-full"><Lock className="w-3 h-3" /> Reach {t} to unlock</span>}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {ads.map((ad) => (
                <div key={ad.id} data-testid={`ad-card-${ad.id}`} className={`bg-white border border-slate-200 rounded-lg p-6 ${locked ? "opacity-60" : ""}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 px-2.5 py-0.5 rounded-full">{ad.format}</span>
                    <button onClick={() => !locked && copyAd(ad)} disabled={locked} data-testid={`copy-ad-${ad.id}`}
                      className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 ${
                        locked ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-slate-900 text-white hover:bg-slate-700"
                      }`}>
                      {locked ? <Lock className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {locked ? "Locked" : "Copy ad"}
                    </button>
                  </div>
                  <p className="font-heading font-semibold text-sm mb-2">{ad.title}</p>
                  <p className={`text-xs text-slate-600 leading-relaxed whitespace-pre-line ${locked ? "blur-[3px] select-none" : ""}`}>{ad.body}</p>
                </div>
              ))}
            </div>
          </section>
        );
      })}
      <p className="text-sm text-slate-500">Want stronger creatives? <Link to="/rewards" className="text-emerald-700 font-medium">See how tiers unlock better ads →</Link></p>
    </main>
  );
}
